export type Volume = {
  num: string;
  range: string;
  title: string;
  pack: string;
  summary: string;
  templates: [string, string][];
};

export const VOLUMES: Volume[] = [
  {
    num: '01',
    range: 'Templates 1.01–1.22',
    title: 'Intake & Assessment',
    pack: 'The First Contact Pack',
    summary: 'Everything from the first phone call to a complete intake summary: adult, child, couples and family intake, biopsychosocial, MSE, risk documentation and safety planning.',
    templates: [
      ['1.01', 'Client Registration Form'],
      ['1.02', 'Initial Phone Screening Form'],
      ['1.03', 'Adult Intake Questionnaire'],
      ['1.04', 'Child & Adolescent Intake (Parent/Guardian)'],
      ['1.05', 'Couples Intake Questionnaire'],
      ['1.06', 'Family Intake Questionnaire'],
      ['1.07', 'Biopsychosocial Assessment'],
      ['1.08', 'Mental Status Exam Checklist'],
      ['1.09', 'Psychosocial & Developmental History'],
      ['1.10', 'Medical History & Current Medications'],
      ['1.11', 'Substance Use History Form'],
      ['1.12', 'Risk Assessment Documentation Form (Clinician-Completed)'],
      ['1.13', 'Safety Planning Worksheet'],
      ['1.14', 'Strengths & Goals Client Questionnaire'],
      ['1.15', 'Cultural & Identity Considerations Form'],
      ['1.16', 'Telehealth Readiness Checklist'],
      ['1.17', 'Emergency Contact Form'],
      ['1.18', 'Collateral Information Form'],
      ['1.19', 'Standardized Measures Record Sheet'],
      ['1.20', 'Intake Summary'],
      ['1.21', 'Waitlist Intake Form'],
      ['1.22', 'Referral Received Log'],
    ],
  },
  {
    num: '02',
    range: 'Templates 2.01–2.22',
    title: 'Consent, Policies & Authorizations',
    pack: 'The Paperwork Pack',
    summary: 'Informed consent, telehealth, minors, couples secrets policy, privacy notice, releases, fee and no-show policies, Good Faith Estimate and more.',
    templates: [
      ['2.01', 'Informed Consent for Psychotherapy'],
      ['2.02', 'Telehealth Informed Consent'],
      ['2.03', 'Consent for Treatment of a Minor'],
      ['2.04', 'Couples & Family Therapy Consent (with Secrets Policy)'],
      ['2.05', 'Group Therapy Consent & Confidentiality Agreement'],
      ['2.06', 'Notice of Privacy Practices'],
      ['2.07', 'Acknowledgment of Receipt of Privacy Notice'],
      ['2.08', 'Authorization to Release or Exchange Information'],
      ['2.09', 'Revocation of Authorization'],
      ['2.10', 'Consent to Audio or Video Recording'],
      ['2.11', 'Email & Text Communication Consent'],
      ['2.12', 'Social Media & Online Contact Policy'],
      ['2.13', 'Cancellation & No-Show Policy'],
      ['2.14', 'Financial Agreement & Fee Policy'],
      ['2.15', 'Good Faith Estimate (Self-Pay Clients)'],
      ['2.16', 'Credit Card Authorization Form'],
      ['2.17', 'Sliding Scale Application & Policy'],
      ['2.18', 'Practice Policies & Procedures (Client Copy)'],
      ['2.19', 'After-Hours & Emergency Contact Policy'],
      ['2.20', 'Termination & Inactive Client Policy'],
      ['2.21', 'Supervisee Disclosure & Consent (Pre-Licensed Clinicians)'],
      ['2.22', 'Professional Disclosure Statement'],
    ],
  },
  {
    num: '03',
    range: 'Templates 3.01–3.22',
    title: 'Session Notes & Clinical Records',
    pack: 'The Documentation Pack',
    summary: 'SOAP, DAP, BIRP, GIRP and PIRP notes, couples, family, group, telehealth and crisis notes, plus logs and trackers.',
    templates: [
      ['3.01', 'SOAP Note'],
      ['3.02', 'DAP Note'],
      ['3.03', 'BIRP Note'],
      ['3.04', 'GIRP Note'],
      ['3.05', 'PIRP Note'],
      ['3.06', 'Narrative Progress Note'],
      ['3.07', 'First Session Note'],
      ['3.08', 'Couples Session Note'],
      ['3.09', 'Family Session Note'],
      ['3.10', 'Group Note (Per Member)'],
      ['3.11', 'Telehealth Session Note'],
      ['3.12', 'Crisis Contact Note'],
      ['3.13', 'Collateral Contact Note'],
      ['3.14', 'No-Show & Late Cancel Note'],
      ['3.15', 'Case Consultation Note'],
      ['3.16', 'Psychotherapy Notes (Separate Record)'],
      ['3.17', 'Late Entry & Addendum Form'],
      ['3.18', 'Client Contact Log'],
      ['3.19', 'Attendance Record'],
      ['3.20', 'Homework & Between-Session Task Tracker'],
      ['3.21', 'Measurement-Based Care Tracker'],
      ['3.22', 'Treatment Plan Review Note'],
    ],
  },
  {
    num: '04',
    range: 'Templates 4.01–4.20',
    title: 'Treatment Planning & Discharge',
    pack: 'The Treatment Pack',
    summary: 'Treatment plans for individuals, children, couples and families, case conceptualization, coordination of care, discharge and letters.',
    templates: [
      ['4.01', 'Individual Treatment Plan'],
      ['4.02', 'Child & Adolescent Treatment Plan'],
      ['4.03', 'Couples Treatment Plan'],
      ['4.04', 'Family Treatment Plan'],
      ['4.05', 'Treatment Plan Update'],
      ['4.06', 'Measurable Goals & Objectives Worksheet'],
      ['4.07', 'Case Conceptualization (General)'],
      ['4.08', 'CBT Case Conceptualization'],
      ['4.09', 'Level of Care Recommendation Form'],
      ['4.10', 'Coordination of Care Letter (PCP or Psychiatrist)'],
      ['4.11', 'Referral Out Letter'],
      ['4.12', 'Transfer of Care Summary'],
      ['4.13', 'Discharge & Termination Summary'],
      ['4.14', 'Closing Letter to Client'],
      ['4.15', 'Aftercare & Relapse Prevention Plan'],
      ['4.16', 'Step-Down Plan'],
      ['4.17', 'Treatment Summary Letter (Client Request)'],
      ['4.18', 'Letter of Attendance & Verification'],
      ['4.19', 'Return to Work or School Support Letter'],
      ['4.20', 'Session Frequency Change Notice'],
    ],
  },
  {
    num: '05',
    range: 'Templates 5.01–5.20',
    title: 'Billing, Insurance & Practice Admin',
    pack: 'The Business Pack',
    summary: 'Superbill, invoice, receipts, insurance verification, prior authorization, income tracking, records requests and practice checklists.',
    templates: [
      ['5.01', 'Superbill'],
      ['5.02', 'Invoice'],
      ['5.03', 'Payment Receipt'],
      ['5.04', 'Fee Schedule'],
      ['5.05', 'Insurance Verification Form'],
      ['5.06', 'Prior Authorization Worksheet'],
      ['5.07', 'Out-of-Network Reimbursement Guide (for Clients)'],
      ['5.08', 'Session & Billing Log'],
      ['5.09', 'Monthly Income & Expense Tracker'],
      ['5.10', 'Accounts Receivable Tracker'],
      ['5.11', 'Caseload Tracker'],
      ['5.12', 'Scheduling & Rescheduling Policy'],
      ['5.13', 'New Client Welcome Packet Letter'],
      ['5.14', 'Records Request Form'],
      ['5.15', 'Records Release Log'],
      ['5.16', 'Privacy Incident Log'],
      ['5.17', 'Annual Practice Checklist'],
      ['5.18', 'CEU & License Renewal Tracker'],
      ['5.19', 'Supervision Hours Log'],
      ['5.20', 'Office or Room Rental Agreement'],
    ],
  },
  {
    num: '06',
    range: 'Templates 6.01–6.20',
    title: 'Client Communication & Professional Letters',
    pack: 'The Communication Pack',
    summary: 'Ready-to-send emails and letters: welcome, reminders, no-shows, rate increases, closures, referrals, schools, physicians and terminations.',
    templates: [
      ['6.01', 'Welcome Email'],
      ['6.02', 'First Session Preparation Email'],
      ['6.03', 'Appointment Reminder Messages'],
      ['6.04', 'No-Show Follow-Up Message'],
      ['6.05', 'Inactive Client Check-In'],
      ['6.06', 'Rate Increase Notice'],
      ['6.07', 'Vacation & Coverage Notice'],
      ['6.08', 'Holiday Closure Notice'],
      ['6.09', 'Insurance or Payment Change Notice'],
      ['6.10', 'Practice Relocation Notice'],
      ['6.11', 'Practice Closing or Retirement Letter'],
      ['6.12', 'Waitlist Message'],
      ['6.13', 'Referral Thank-You Letter'],
      ['6.14', 'Introduction Letter to Physicians'],
      ['6.15', 'Letter to School (with Authorization)'],
      ['6.16', 'Collaboration Letter to Psychiatrist'],
      ['6.17', 'Client Feedback Survey'],
      ['6.18', 'Professional Reference Letter'],
      ['6.19', 'Telehealth Link & Tech Troubleshooting Email'],
      ['6.20', 'Termination Letter (Non-Attendance)'],
    ],
  },
];

export const PAINS = [
  { tag: 'AFTER HOURS', title: 'Notes at 10 p.m. Again.', text: 'Your last session ended hours ago, and you are still writing documentation at the kitchen table.' },
  { tag: 'FROM SCRATCH', title: 'Rebuilding the same forms', text: 'Every new client, policy or letter means opening a blank document and starting over.' },
  { tag: 'NEW PRACTICE', title: 'Opening on your own', text: 'Leaving the agency means building intake, consent, billing and policies with no one to hand you the paperwork.' },
  { tag: 'OLD TEMPLATES', title: 'Forms that no longer fit', text: 'Outdated versions, missing telehealth sections and no Good Faith Estimate for self-pay clients.' },
  { tag: 'LETTERS', title: 'That letter you keep postponing', text: 'Schools, physicians, rate increases, vacations, closures: every one takes longer than the session itself.' },
  { tag: 'ENERGY', title: 'Admin eats clinical time', text: 'Hours that could go to clients, supervision or rest go to paperwork.' },
];

export const INSIDE = [
  { title: 'Ready to fill in', text: 'Clear sections, fields, checkboxes, tables and signature lines, laid out the way clinicians actually use them.' },
  { title: 'Before you use it', text: 'A short note on every template telling you when and how to use it. Delete it and the form is ready.' },
  { title: 'Your practice details', text: '[Practice Name], [Clinician Name, Credentials] and other bracketed fields to replace once.' },
  { title: 'Editable Word + PDF', text: 'Edit the Word version for your practice or print the PDF and use it today.' },
  { title: 'Telehealth included', text: 'Telehealth consent, readiness checklist, session note and tech troubleshooting email.' },
  { title: 'Adapt to your state', text: 'Templates only: adapt each one to your state laws and licensing board requirements.' },
];

export const PREVIEW = {
  title: 'TEMPLATE 3.01 · SOAP Note',
  goal: 'A complete, structured progress note you can fill in during or right after the session.',
  bestFor: 'Individual sessions, in person or telehealth, when your practice or payer prefers SOAP format.',
  time: 'Category 3 · Session Notes & Clinical Records · Word + PDF',
  steps: [
    'Header: client name or ID, date of service, start and end time, CPT code, modality and location.',
    'Subjective: presenting concerns, client report and progress since the last session, in the client’s words.',
    'Objective: appearance, behavior, mood and affect, and any measure administered.',
    'Assessment: your clinical impression, progress toward treatment plan goals and risk documentation.',
    'Plan: interventions for next session, homework, referrals, coordination of care and next appointment.',
  ],
  questions: [
    'Checkbox rows for modality and risk so you document faster.',
    'Clinician signature, credentials and date lines.',
    'Matching DAP, BIRP, GIRP and PIRP versions in the same category.',
  ],
};

export const BONUSES = [
  { short: 'Phrase Bank', title: 'The 1,000+ Progress Note Phrase Bank™', text: '1,105 professional, neutral phrases for every part of a progress note, organized by section and by modality.', value: 32 },
  { short: 'Client Worksheets', title: 'The Client Worksheets & Homework Pack™', text: '40 printable between-session worksheets: thought record, values, sleep log, grounding, coping plan, mood tracker and more.', value: 29 },
  { short: 'Launch Checklist', title: 'The Private Practice Launch Checklist™', text: '157 step-by-step tasks in 15 phases, from license to first client: NPI, CAQH, insurance, EHR, policies, fees and marketing.', value: 32 },
  { short: 'AI Quick Guide', title: 'The AI Documentation Quick Guide™', text: '30 copy-and-paste AI prompts to complete the templates faster, with safe-use rules for de-identified information.', value: 27 },
];

export const STEPS = [
  { n: '1', title: 'Download the kit', text: 'Instant access after checkout to every template in Word and PDF, plus a START HERE guide.' },
  { n: '2', title: 'Add your practice', text: 'Replace the bracketed fields with your practice name, credentials and contact details.' },
  { n: '3', title: 'Use it today', text: 'Adapt to your state and licensing board, then use it with your next client.' },
];

export const FAQ = [
  { q: 'Who is this for?', a: 'Licensed therapists, counselors, clinical social workers, MFTs and psychologists in private practice or about to open one, and pre-licensed clinicians working under supervision.' },
  { q: 'What exactly do I get?', a: '126 templates in 6 categories, each in editable Word and PDF, a START HERE guide with the full index, and 4 bonuses, as an instant digital download.' },
  { q: 'Can I edit the templates?', a: 'Yes. Every template comes as an editable Word document so you can add your practice details, change wording and adapt it to your workflow.' },
  { q: 'Are these approved for my state?', a: 'They are templates, not legal advice. Requirements vary by state, payer and licensing board, so review and adapt each one to your state laws and licensing board requirements before use.' },
  { q: 'Can I use it with telehealth clients?', a: 'Yes. The kit includes a telehealth informed consent, a readiness checklist, a telehealth session note and a tech troubleshooting email.' },
  { q: 'Do I need special software?', a: 'No. Open the Word files in Microsoft Word, Google Docs or any compatible editor, or print the PDF versions.' },
  { q: 'What if it is not for me?', a: 'You have a 7-day risk-free guarantee. If the kit does not fit your practice, email us within 7 days of purchase and you get your money back.' },
];
